import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiPrinter,
  FiAward,
  FiAlertCircle,
  FiUser,
  FiHash,
} from "react-icons/fi";
import { resultAPI } from "../services/api";
import { getGradeColor, calculateGrade } from "../utils/helpers";

const EXAM_TYPES = [
  "First Terminal",
  "Second Terminal",
  "Final Examination",
  "Pre-Board",
  "Unit Test",
];
const CLASSES = [
  "Nursery",
  "KG",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
];

const Results = () => {
  const [form, setForm] = useState({
    rollNumber: "",
    class: "",
    section: "",
    examType: "",
    academicYear: "",
  });
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  // Fetch distinct sections whenever class changes
  useEffect(() => {
    if (!form.class) { setSections([]); return; }
    setSectionsLoading(true);
    setForm((prev) => ({ ...prev, section: "" }));
    resultAPI.getSections({ class: form.class })
      .then((res) => {
        const list = res.data?.data?.sections || [];
        setSections(list);
        if (list.length === 1) setForm((prev) => ({ ...prev, section: list[0] }));
      })
      .catch(() => setSections([]))
      .finally(() => setSectionsLoading(false));
  }, [form.class]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!form.rollNumber.trim()) {
      setError("Please enter a roll number");
      return;
    }
    if (!form.class) {
      setError("Please select a class");
      return;
    }
    if (!form.examType) {
      setError("Please select an exam type");
      return;
    }

    setLoading(true);
    setResult(null);
    setSearched(true);

    try {
      const params = {
        rollNumber: form.rollNumber,
        class: form.class,
        examType: form.examType,
      };
      if (form.section) params.section = form.section;
      if (form.academicYear) params.academicYear = form.academicYear;
      const response = await resultAPI.search(params);
      const list = response.data?.data?.results;
      const data = Array.isArray(list) ? list[0] : response.data?.data?.result;
      if (data) {
        setResult(data);
      } else {
        setError(
          "No published result found for the provided details. Please verify and try again.",
        );
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(
          "No result found for the provided details. Please check your roll number and try again.",
        );
      } else {
        setError("Unable to fetch results. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  // Computed values
  const subjects = result?.subjects || [];
  const totalFull = subjects.reduce((s, sub) => s + (sub.fullMarks || 0), 0);
  const totalObtained = subjects.reduce(
    (s, sub) => s + (sub.obtainedMarks || 0),
    0,
  );
  const percentage =
    result?.percentage ??
    (totalFull > 0 ? ((totalObtained / totalFull) * 100).toFixed(2) : 0);
  const overallGrade = result?.grade || calculateGrade(parseFloat(percentage));
  const passed = subjects.every((sub) => sub.obtainedMarks >= sub.passMarks);

  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Academic Results
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Result Portal
          </h1>
          <p className="text-white/90 text-lg max-w-xl mx-auto">
            Enter your roll number to view your examination results
          </p>
        </motion.div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Form */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mb-8 no-print">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary-50 border border-primary-100 rounded-xl flex items-center justify-center">
              <FiSearch className="w-5 h-5 text-primary-500" />
            </div>
            <h2 className="text-xl font-bold text-primary-500 font-serif">
              Search Your Result
            </h2>
          </div>

          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Roll Number *
              </label>
              <input
                name="rollNumber"
                value={form.rollNumber}
                onChange={handleChange}
                placeholder="e.g. GA-2024-042"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class *
                </label>
                <select
                  name="class"
                  value={form.class}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 bg-white"
                >
                  <option value="">Select Class</option>
                  {CLASSES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  name="section"
                  value={form.section}
                  onChange={handleChange}
                  disabled={!form.class || sectionsLoading}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 bg-white disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {sectionsLoading ? (
                    <option>Loading...</option>
                  ) : (
                    <>
                      <option value="">
                        {!form.class
                          ? "Select class first"
                          : sections.length === 0
                          ? "No sections found"
                          : "All Sections"}
                      </option>
                      {sections.map((s) => (
                        <option key={s} value={s}>
                          Section {s}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam Type *
              </label>
              <select
                name="examType"
                value={form.examType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 bg-white"
              >
                <option value="">Select Exam</option>
                {EXAM_TYPES.map((e) => (
                  <option key={e}>{e}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Academic Year{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                name="academicYear"
                value={form.academicYear}
                onChange={handleChange}
                placeholder="e.g. 2024-2025"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-70"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <FiSearch className="w-4 h-4" />
                  Search Result
                </>
              )}
            </button>
          </form>
        </div>

        {/* Result Marksheet */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden print-card"
              id="result-card"
            >
              {/* Marksheet Header */}
              <div className="hero-gradient p-8 text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <FiAward className="w-6 h-6 text-secondary-400" />
                  <h2 className="text-white font-serif font-bold text-xl">
                    Maria Education Foundation
                  </h2>
                </div>
                <p className="text-white/70 text-xs uppercase tracking-widest mb-1">
                  Official Mark Sheet
                </p>
                <p className="text-white/80 text-sm">
                  {result.examType} — Academic Year: {result.academicYear}
                </p>
              </div>

              {/* Student Info */}
              <div className="p-6 border-b border-gray-100">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5 flex items-center gap-1">
                      <FiUser className="w-3 h-3" /> Student Name
                    </p>
                    <p className="font-bold text-primary-500">
                      {result.studentName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5 flex items-center gap-1">
                      <FiHash className="w-3 h-3" /> Roll Number
                    </p>
                    <p className="font-bold text-primary-500">
                      {result.rollNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">
                      Class & Section
                    </p>
                    <p className="font-bold text-primary-500">
                      {result.class}
                      {result.section ? ` - ${result.section}` : ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">
                      Academic Year
                    </p>
                    <p className="font-bold text-primary-500">
                      {result.academicYear}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subject-wise Marks */}
              <div className="p-6">
                <h3 className="font-bold text-primary-500 mb-4 font-serif">
                  Subject-wise Results
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border border-gray-100">
                        <th className="text-left px-4 py-3 text-gray-600 font-semibold rounded-l-xl">
                          Subject
                        </th>
                        <th className="text-center px-4 py-3 text-gray-600 font-semibold">
                          Full Marks
                        </th>
                        <th className="text-center px-4 py-3 text-gray-600 font-semibold">
                          Pass Marks
                        </th>
                        <th className="text-center px-4 py-3 text-gray-600 font-semibold">
                          Obtained
                        </th>
                        <th className="text-center px-4 py-3 text-gray-600 font-semibold rounded-r-xl">
                          Grade
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map((sub, idx) => {
                        const subPct =
                          sub.fullMarks > 0
                            ? (sub.obtainedMarks / sub.fullMarks) * 100
                            : 0;
                        const subGrade = sub.grade || calculateGrade(subPct);
                        const subPassed = sub.obtainedMarks >= sub.passMarks;
                        return (
                          <tr
                            key={idx}
                            className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {sub.name}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600">
                              {sub.fullMarks}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-600">
                              {sub.passMarks}
                            </td>
                            <td
                              className={`px-4 py-3 text-center font-bold ${subPassed ? "text-gray-800" : "text-red-500"}`}
                            >
                              {sub.obtainedMarks}
                              {!subPassed && (
                                <span className="text-xs ml-1">(F)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-bold ${getGradeColor(subGrade)}`}
                              >
                                {subGrade}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-primary-50 border-t-2 border-primary-100">
                        <td className="px-4 py-3 font-bold text-primary-700">
                          Total
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-primary-700">
                          {totalFull}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400">
                          —
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-primary-700">
                          {totalObtained}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-400">
                          —
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Summary Cards */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 text-center">
                    <p className="text-gray-500 text-xs mb-1">Total Marks</p>
                    <p className="text-primary-500 font-bold text-lg">
                      {totalObtained}/{totalFull}
                    </p>
                  </div>
                  <div className="bg-secondary-50 border border-secondary-100 rounded-2xl p-4 text-center">
                    <p className="text-gray-500 text-xs mb-1">Percentage</p>
                    <p className="text-secondary-600 font-bold text-lg">
                      {parseFloat(percentage).toFixed(2)}%
                    </p>
                  </div>
                  <div
                    className={`${getGradeColor(overallGrade)} border rounded-2xl p-4 text-center`}
                  >
                    <p className="text-xs mb-1">Overall Grade</p>
                    <p className="font-bold text-2xl">{overallGrade}</p>
                  </div>
                  <div
                    className={`${passed ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"} border rounded-2xl p-4 text-center`}
                  >
                    <p className="text-xs mb-1">Result</p>
                    <p className="font-bold text-lg">
                      {passed ? "PASSED" : "FAILED"}
                    </p>
                  </div>
                </div>

                {/* Position badges (if available) */}
                {(result.positionInClass ||
                  result.positionInSection ||
                  result.rank) && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {result.positionInClass && (
                      <div className="flex items-center gap-2 bg-accent-50 border border-accent-200 rounded-xl px-4 py-2">
                        <FiAward className="w-4 h-4 text-accent-600" />
                        <span className="text-sm font-semibold text-accent-700">
                          Position in Class:{" "}
                          <span className="text-lg font-bold">
                            {result.positionInClass}
                          </span>
                        </span>
                      </div>
                    )}
                    {result.positionInSection && (
                      <div className="flex items-center gap-2 bg-accent-50 border border-accent-200 rounded-xl px-4 py-2">
                        <FiAward className="w-4 h-4 text-accent-600" />
                        <span className="text-sm font-semibold text-accent-700">
                          Position in Section:{" "}
                          <span className="text-lg font-bold">
                            {result.positionInSection}
                          </span>
                        </span>
                      </div>
                    )}
                    {result.rank && !result.positionInClass && (
                      <div className="flex items-center gap-2 bg-accent-50 border border-accent-200 rounded-xl px-4 py-2">
                        <FiAward className="w-4 h-4 text-accent-600" />
                        <span className="text-sm font-semibold text-accent-700">
                          Rank:{" "}
                          <span className="text-lg font-bold">
                            {result.rank}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Remarks */}
                {result.remarks && (
                  <div className="mt-4 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-500 mb-0.5">Remarks</p>
                    <p className="text-sm font-medium text-gray-700">
                      {result.remarks}
                    </p>
                  </div>
                )}

                {/* Print Button */}
                <div className="mt-6 flex justify-end no-print">
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
                  >
                    <FiPrinter className="w-4 h-4" />
                    Print / Save as PDF
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>
    </div>
  );
};

export default Results;
